var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { BButtonGroup, BCol, BContainer, BRow } from "bootstrap-vue";
import { computed, ref, watch } from "vue";
import {} from "vue";
import { GalaxyApi } from "@/api";
import { getAppRoot } from "@/onload/loadConfig";
import { errorMessageAsString } from "@/utils/simple-error";
import HelpText from "@/components/Help/HelpText.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'WorkflowInvocationMetrics',
    props: {
        invocationId: {
            type: String,
            required: true,
        },
    },
    setup(__props) {
        const props = __props;
        const VegaWrapper = () => import("./VegaWrapper.vue");
        const groupBy = ref("tool_id");
        const timing = ref("seconds");
        const jobMetrics = ref();
        const fetchError = ref();
        const attributeToLabel = {
            tool_id: "Tool ID",
            step_id: "Step",
        };
        function fetchMetrics() {
            return __awaiter(this, void 0, void 0, function* () {
                const { data, error } = yield GalaxyApi().GET("/api/invocations/{invocation_id}/metrics", {
                    params: {
                        path: {
                            invocation_id: props.invocationId,
                        },
                    },
                });
                if (error) {
                    fetchError.value = errorMessageAsString(error);
                }
                else {
                    jobMetrics.value = data;
                }
            });
        }
        watch(props, () => fetchMetrics(), { immediate: true });
        function itemToX(item) {
            if (groupBy.value === "tool_id") {
                return item.tool_id;
            }
            else if (groupBy.value === "step_id") {
                return `${item.step_index + 1}: ${item.step_label || item.tool_id}`;
            }
            else {
                throw Error("Cannot happen");
            }
        }
        function computeAllocatedCoreTime(jobMetrics) {
            const walltimePerJob = {};
            const coresPerJob = {};
            const jobInfo = {};
            jobMetrics === null || jobMetrics === void 0 ? void 0 : jobMetrics.forEach((jobMetric) => {
                if (jobMetric.name == "runtime_seconds") {
                    walltimePerJob[jobMetric.job_id] = parseFloat(jobMetric.raw_value);
                    jobInfo[jobMetric.job_id] = {
                        toolId: jobMetric.tool_id,
                        stepIndex: jobMetric.step_index,
                        stepLabel: jobMetric.step_label,
                    };
                }
                else if (jobMetric.name == "galaxy_slots") {
                    coresPerJob[jobMetric.job_id] = parseFloat(jobMetric.raw_value);
                }
            });
            const thisMetric = [];
            Object.keys(walltimePerJob).map((jobId) => {
                const walltime = walltimePerJob[jobId];
                const cores = coresPerJob[jobId];
                const thisJobInfo = jobInfo[jobId];
                if (cores && thisJobInfo && walltime) {
                    const metric = {
                        name: "allocated_core_time",
                        job_id: jobId,
                        raw_value: (cores * walltime).toString(),
                        tool_id: thisJobInfo.toolId,
                        step_index: thisJobInfo.stepIndex,
                        step_label: thisJobInfo.stepLabel,
                    };
                    thisMetric.push(metric);
                }
            });
            return thisMetric;
        }
        function metricToSpecData(jobMetrics, metricName, yTitle, helpTerm, transform) {
            const thisMetric = jobMetrics === null || jobMetrics === void 0 ? void 0 : jobMetrics.filter((jobMetric) => jobMetric.name == metricName);
            const values = thisMetric === null || thisMetric === void 0 ? void 0 : thisMetric.map((item) => {
                let y = parseFloat(item.raw_value);
                if (transform !== undefined) {
                    y = transform(y);
                }
                return {
                    y,
                    x: itemToX(item),
                    job_id: item.job_id,
                    tooltip: "click to view job",
                };
            });
            return {
                x_title: attributeToLabel[groupBy.value],
                y_title: yTitle,
                helpTerm,
                values,
            };
        }
        function metricToAggregateData(jobMetrics, metricName, yTitle, helpTerm, transform) {
            const thisMetric = jobMetrics === null || jobMetrics === void 0 ? void 0 : jobMetrics.filter((jobMetric) => jobMetric.name == metricName);
            const aggregateByX = {};
            thisMetric === null || thisMetric === void 0 ? void 0 : thisMetric.forEach((item) => {
                let y = parseFloat(item.raw_value);
                if (transform !== undefined) {
                    y = transform(y);
                }
                const x = itemToX(item);
                if (!(x in aggregateByX)) {
                    aggregateByX[x] = 0;
                }
                if (x in aggregateByX) {
                    aggregateByX[x] += y;
                }
            });
            const values = Object.keys(aggregateByX).map((key) => {
                const value = aggregateByX[key];
                return {
                    category: key,
                    y: value || 0,
                };
            });
            return {
                category_title: attributeToLabel[groupBy.value],
                y_title: yTitle,
                helpTerm,
                values,
            };
        }
        function transformTime(time) {
            if (timing.value == "minutes") {
                return time / 60;
            }
            else if (timing.value == "hours") {
                return time / 3600;
            }
            else {
                return time;
            }
        }
        const allocatedCoreTime = computed(() => {
            return computeAllocatedCoreTime(jobMetrics.value);
        });
        const wallclock = computed(() => {
            const title = `Runtime (in ${timingInTitles.value})`;
            return metricToSpecData(jobMetrics.value, "runtime_seconds", title, "galaxy.jobs.metrics.walltime", transformTime);
        });
        const wallclockAggregate = computed(() => {
            const title = `Runtime (in ${timingInTitles.value})`;
            return metricToAggregateData(jobMetrics.value, "runtime_seconds", title, "galaxy.jobs.metrics.walltime", transformTime);
        });
        const allocatedCoreTimeSpec = computed(() => {
            const title = `Allocated Core Time (in ${timingInTitles.value})`;
            return metricToSpecData(allocatedCoreTime.value, "allocated_core_time", title, "galaxy.jobs.metrics.allocated_core_time", transformTime);
        });
        const allocatedCoreTimeAggregate = computed(() => {
            const title = `Allocated Core Time (in ${timingInTitles.value})`;
            return metricToAggregateData(allocatedCoreTime.value, "allocated_core_time", title, "galaxy.jobs.metrics.allocated_core_time", transformTime);
        });
        const coresAllocated = computed(() => {
            return metricToSpecData(jobMetrics.value, "galaxy_slots", "Cores Allocated", "galaxy.jobs.metrics.cores");
        });
        const memoryAllocated = computed(() => {
            return metricToSpecData(jobMetrics.value, "galaxy_memory_mb", "Memory Allocated (in MB)");
        });
        const peakMemory = computed(() => {
            return metricToSpecData(jobMetrics.value, "memory.peak", "Max memory usage recorded (in MB)", undefined, (v) => v / 1024 ** 2);
        });
        function itemToPieChartSpec(item) {
            const spec = {
                $schema: "https://vega.github.io/schema/vega-lite/v5.json",
                description: "Aggregate data.",
                data: {
                    values: item.values,
                },
                mark: { type: "arc", tooltip: true },
                encoding: {
                    theta: { field: "y", type: "quantitative" },
                    color: {
                        field: "category",
                        type: "nominal",
                        legend: {
                            type: "symbol",
                            title: item.category_title,
                            titleFontSize: 16,
                            labelFontSize: 14,
                        },
                    },
                    tooltip: [
                        { field: "category", type: "nominal", title: item.category_title },
                        { field: "y", type: "quantitative", title: item.y_title },
                    ],
                },
                autosize: {
                    type: "fit",
                    resize: true,
                },
                width: 400,
                height: 250,
            };
            return spec;
        }
        function itemToSpec(item) {
            const spec = {
                $schema: "https://vega.github.io/schema/vega-lite/v5.json",
                description: "A boxplot with jittered points.",
                data: {
                    values: item.values,
                },
                transform: [
                    {
                        calculate: "random() - 0.5",
                        as: "random_jitter",
                    },
                    {
                        calculate: "'" + getAppRoot() + "jobs/' + datum.job_id + '/view'",
                        as: "url",
                    },
                ],
                encoding: {
                    y: {
                        field: "y",
                        type: "quantitative",
                        scale: { zero: false },
                        title: item.y_title,
                    },
                    x: {
                        field: "x",
                        type: "nominal",
                        title: item.x_title,
                        axis: {
                            labelAngle: -45,
                            labelAlign: "right",
                        },
                    },
                },
                layer: [
                    {
                        mark: { type: "boxplot", opacity: 0.5 },
                        width: "container",
                    },
                    {
                        mark: {
                            type: "point",
                            opacity: 0.7,
                        },
                        encoding: {
                            xOffset: { field: "random_jitter", type: "quantitative", scale: { domain: [-2, 2] } },
                            tooltip: { field: "tooltip", type: "nominal" },
                            href: { field: "url", type: "nominal" },
                            color: { field: "x", type: "nominal", legend: null },
                        },
                        width: "container",
                        height: 350,
                    },
                ],
            };
            return spec;
        }
        const metrics = computed(() => {
            const items = [
                wallclock.value,
                allocatedCoreTimeSpec.value,
                coresAllocated.value,
                memoryAllocated.value,
                peakMemory.value,
            ].filter((item) => { var _a; return (_a = item.values) === null || _a === void 0 ? void 0 : _a.length; });
            return Object.fromEntries(items.map((item) => [item.y_title, { spec: itemToSpec(item), item: item }]));
        });
        function getTimingInTitle(timing) {
            return timing.charAt(0).toUpperCase() + timing.slice(1);
        }
        const timingInTitles = computed(() => {
            return getTimingInTitle(timing.value);
        });
        const groupByInTitles = computed(() => {
            return attributeToLabel[groupBy.value];
        });
        return { __sfc: true, VegaWrapper, props, groupBy, timing, jobMetrics, fetchError, attributeToLabel, fetchMetrics, itemToX, computeAllocatedCoreTime, metricToSpecData, metricToAggregateData, transformTime, allocatedCoreTime, wallclock, wallclockAggregate, allocatedCoreTimeSpec, allocatedCoreTimeAggregate, coresAllocated, memoryAllocated, peakMemory, itemToPieChartSpec, itemToSpec, metrics, getTimingInTitle, timingInTitles, groupByInTitles, BButtonGroup, BCol, BContainer, BRow, HelpText };
    }
});
